// pages/About.js
import React from 'react';
import { Container, Typography, Grid, Box, Card, Chip, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import { Groups, EmojiEvents, TrendingUp, Security, Support, Public, Rocket, TrackChanges } from '@mui/icons-material';

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const stats = [
    { number: '50+', label: 'Projects', icon: <EmojiEvents />, color: '#FF6B6B' },
    { number: '15+', label: 'Team Members', icon: <Groups />, color: '#4ECDC4' },
    { number: '5', label: 'Countries', icon: <Public />, color: '#45B7D1' },
    { number: '99%', label: 'Satisfaction', icon: <TrendingUp />, color: '#96CEB4' }
  ];

  const values = [
    {
      icon: <Security />,
      title: 'Security First',
      description: 'Enterprise-grade security for your digital assets'
    },
    {
      icon: <Rocket />,
      title: 'Innovation',
      description: 'Cutting-edge technologies and modern practices'
    },
    {
      icon: <Support />,
      title: 'Partnership',
      description: 'Long-term relationships through transparency'
    },
    {
      icon: <TrackChanges />,
      title: 'Excellence',
      description: 'Rigorous quality assurance processes'
    }
  ];

  const technologies = [
    { name: 'React', desc: 'JavaScript library for building dynamic UI components.' },
    { name: 'Next.js', desc: 'Full-stack React framework for server-rendered web apps.' },
    { name: 'Node.js', desc: 'JavaScript runtime for scalable backend applications.' },
    { name: 'Python', desc: 'Versatile language for backend, data science, and AI.' },
    { name: 'TypeScript', desc: 'Typed superset of JavaScript for cleaner, maintainable code.' },
    { name: 'Tailwind CSS', desc: 'Utility-first CSS framework for modern responsive design.' },
    { name: 'Express.js', desc: 'Fast and minimal web framework for Node.js.' },
    { name: 'GraphQL', desc: 'Flexible API query language for efficient data fetching.' },
    { name: 'MongoDB', desc: 'NoSQL database for scalable, document-based data storage.' },
    { name: 'PostgreSQL', desc: 'Advanced open-source SQL database with modern features.' },
    { name: 'AWS', desc: 'Cloud platform for hosting, storage, and scalable infrastructure.' },
    { name: 'Docker', desc: 'Containerization platform for consistent app deployment.' },
    { name: 'Kubernetes', desc: 'Container orchestration system for automated scaling and management.' },
    { name: 'Vercel', desc: 'Serverless deployment platform optimized for React and Next.js.' },
    { name: 'React Native', desc: 'Framework for building native mobile apps using React.' },
    { name: 'Flutter', desc: 'Cross-platform UI toolkit for web and mobile from Google.' },
    { name: 'GitHub Actions', desc: 'CI/CD automation for testing, building, and deployment.' },
    { name: 'Firebase', desc: 'Google-backed platform for real-time database and hosting.' },
    { name: 'TensorFlow', desc: 'Machine learning library for deep learning and AI applications.' },
    { name: 'OpenAI API', desc: 'AI-powered API for chatbots, content generation, and automation.' },
    { name: 'Zoho', desc: 'Cloud-based suite offering CRM, finance, and business automation tools.' }
  ];


  const globalLocations = [
    { country: 'India', top: '58%', left: '75%' },
    { country: 'Canada', top: '30%', left: '20%' },
    { country: 'USA', top: '42%', left: '18%' },
    { country: 'Australia', top: '72%', left: '87%' },
    { country: 'Singapore', top: '60%', left: '78%' }
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const pulse = {
    pulse: {
      scale: [1, 1.1, 1],
      opacity: [0.7, 1, 0.7],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const float = {
    float: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1128 0%, #0f1a3a 50%, #1a2342 100%)',
        py: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        animate={float}
        style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '100px',
          height: '100px',
          background: 'radial-gradient(circle, rgba(139, 0, 0, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
        }}
      />
      <motion.div
        animate={{
          y: [0, -15, 0],
          transition: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }
        }}
        style={{
          position: 'absolute',
          bottom: '25%',
          right: '15%',
          width: '80px',
          height: '80px',
          background: 'radial-gradient(circle, rgba(139, 0, 0, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: '900',
                background: 'linear-gradient(45deg, #8b0000, #ff4d4d, #ff6666)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                fontSize: { xs: '3rem', md: '4rem' },
                mb: 2,
              }}
            >
              About Us
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "rgba(255,255,255,0.8)",
                fontWeight: 300,
                maxWidth: "600px",
                mx: "auto",
                textAlign: "center",
                lineHeight: 1.6
              }}
            >
              Building the future, one line of code at a time
            </Typography>

            <Typography
              sx={{
                mt: 1.5,
                fontSize: "0.85rem",
                color: "rgba(255,255,255,0.65)",
                fontStyle: "italic",
                textAlign: "center",
                letterSpacing: "0.03em"
              }}
            >
              “எண்ணென்ப ஏனை எழுத்தென்ப இவ்விரண்டும்
              கண்ணென்ப வாழும் உயிர்க்கு”
            </Typography>

            <Typography
              sx={{
                mt: 0.4,
                fontSize: "0.75rem",
                color: "rgba(255,255,255,0.55)",
                textAlign: "center"
              }}
            >
              “Numbers and letters are the eyes through which life perceives the world.”
            </Typography>

          </Box>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <motion.div variants={fadeInUp}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, rgba(139,0,0,0.1) 0%, rgba(139,0,0,0.05) 100%)',
                      border: '1px solid rgba(139,0,0,0.3)',
                      borderRadius: 3,
                      p: 3,
                      textAlign: 'center',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      style={{ color: stat.color, marginBottom: '1rem' }}
                    >
                      {stat.icon}
                    </motion.div>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: '800',
                        background: `linear-gradient(45deg, ${stat.color}, ${stat.color}dd)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                        mb: 1,
                      }}
                    >
                      {stat.number}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.8)',
                        fontWeight: '600',
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Section 1: Our Journey */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          sx={{ mb: 8 }}
        >
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(139,0,0,0.1) 0%, rgba(139,0,0,0.05) 100%)',
              border: '1px solid rgba(139,0,0,0.3)',
              borderRadius: 4,
              p: 4,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: '800',
                background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 3,
                textAlign: 'center',
              }}
            >
              Our Journey
            </Typography>

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                      borderRadius: 3,
                      p: 3,
                      textAlign: 'center',
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '1.2rem',
                    }}
                  >
                    From Vision to Global Impact
                  </Box>
                </motion.div>

                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    lineHeight: 1.8,
                    mt: 3,
                    textAlign: 'center',
                  }}
                >
                  Founded in 2023, Zolang Nexora began as a passionate team of developers
                  with a vision to deliver exceptional digital solutions. Today, we serve
                  clients globally with innovative technology and unwavering commitment to excellence.
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <motion.div
                  animate={{
                    y: [0, -10, 0],
                    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      height: '200px',
                      background: 'rgba(10, 17, 40, 0.5)',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid rgba(139, 0, 0, 0.3)',
                    }}
                  >
                    {/* Simple progress bar visualization */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '10%',
                        right: '10%',
                        height: '4px',
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '2px',
                      }}
                    />
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '80%' }}
                      transition={{ duration: 2, ease: "easeOut" }}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '10%',
                        height: '4px',
                        background: 'linear-gradient(90deg, #8b0000, #ff4d4d)',
                        borderRadius: '2px',
                      }}
                    />

                    {/* Milestone markers */}
                    {[0, 25, 50, 75, 100].map((position, index) => (
                      <motion.div
                        key={index}
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.3 }}
                        style={{
                          position: 'absolute',
                          top: '45%',
                          left: `${10 + position * 0.8}%`,
                          width: '12px',
                          height: '12px',
                          background: '#ff4d4d',
                          borderRadius: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      />
                    ))}
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Card>
        </motion.div>

        {/* Section 2: Mission & Vision */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          sx={{ mb: 8 }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(139,0,0,0.15) 0%, rgba(139,0,0,0.05) 100%)',
                    border: '1px solid rgba(139,0,0,0.4)',
                    borderRadius: 3,
                    p: 4,
                    height: '100%',
                    textAlign: 'center',
                  }}
                >
                  <motion.div
                    animate={{ rotate: [0, 10, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Rocket sx={{ fontSize: 60, color: '#ff4d4d', mb: 2 }} />
                  </motion.div>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ff4d4d',
                      fontWeight: '700',
                      mb: 2,
                    }}
                  >
                    Our Mission
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      lineHeight: 1.7,
                      fontStyle: 'italic',
                    }}
                  >
                    To empower businesses with innovative technology solutions that drive growth,
                    enhance efficiency, and create lasting competitive advantages.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(139,0,0,0.15) 0%, rgba(139,0,0,0.05) 100%)',
                    border: '1px solid rgba(139,0,0,0.4)',
                    borderRadius: 3,
                    p: 4,
                    height: '100%',
                    textAlign: 'center',
                  }}
                >
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <TrackChanges sx={{ fontSize: 60, color: '#ff4d4d', mb: 2 }} />
                  </motion.div>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ff4d4d',
                      fontWeight: '700',
                      mb: 2,
                    }}
                  >
                    Our Vision
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      lineHeight: 1.7,
                      fontStyle: 'italic',
                    }}
                  >
                    To be the most trusted technology partner, recognized for delivering
                    exceptional digital experiences that transform businesses worldwide.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>

        {/* Section 3: Global Presence */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          sx={{ mb: 8 }}
        >
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(139,0,0,0.1) 0%, rgba(139,0,0,0.05) 100%)',
              border: '1px solid rgba(139,0,0,0.3)',
              borderRadius: 4,
              p: 4,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: '800',
                background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 3,
                textAlign: 'center',
              }}
            >
              Global Reach
            </Typography>

            <Box sx={{ position: 'relative', height: '300px', mb: 3 }}>
              {/* World Map Background */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, #1a2342 0%, #0f1a3a 100%)',
                  opacity: 0.8,
                  borderRadius: 2,
                }}
              />

              {/* Location Dots */}
              {globalLocations.map((location, index) => (
                <motion.div
                  key={location.country}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  style={{
                    position: 'absolute',
                    top: location.top,
                    left: location.left,
                  }}
                >
                  <motion.div
                    animate="pulse"
                    variants={pulse}
                  >
                    <Box
                      sx={{
                        width: '16px',
                        height: '16px',
                        background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                        borderRadius: '50%',
                        boxShadow: '0 0 20px rgba(139, 0, 0, 0.8)',
                        cursor: 'pointer',
                        position: 'relative',
                      }}
                    />
                  </motion.div>

                  {/* Country Label */}
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      top: '20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: '#ff4d4d',
                      fontWeight: '700',
                      whiteSpace: 'nowrap',
                      background: 'rgba(10, 17, 40, 0.9)',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid rgba(139, 0, 0, 0.5)',
                    }}
                  >
                    {location.country}
                  </Typography>
                </motion.div>
              ))}
            </Box>

            {/* Location List */}
            <Grid container spacing={2} justifyContent="center">
              {globalLocations.map((location, index) => (
                <Grid item key={index}>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Chip
                      icon={<Public />}
                      label={location.country}
                      sx={{
                        background: 'linear-gradient(135deg, rgba(139,0,0,0.3), rgba(139,0,0,0.1))',
                        color: 'rgba(255,255,255,0.9)',
                        border: '1px solid rgba(139,0,0,0.4)',
                        fontWeight: '600',
                      }}
                    />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Card>
        </motion.div>

        {/* Section 4: Values */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          sx={{ mb: 8 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: '800',
              background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              mb: 4,
              textAlign: 'center',
            }}
          >
            Our Values
          </Typography>
          <Grid container spacing={3}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div variants={fadeInUp}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, rgba(139,0,0,0.1) 0%, rgba(139,0,0,0.05) 100%)',
                      border: '1px solid rgba(139,0,0,0.3)',
                      borderRadius: 3,
                      p: 3,
                      textAlign: 'center',
                      height: '100%',
                    }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 5 }}
                      style={{ color: '#ff4d4d', marginBottom: '1rem' }}
                    >
                      {value.icon}
                    </motion.div>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'rgba(255,255,255,0.95)',
                        fontWeight: '700',
                        mb: 1,
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.8)',
                        lineHeight: 1.5,
                      }}
                    >
                      {value.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Section 5: Technologies */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(139,0,0,0.1) 0%, rgba(139,0,0,0.05) 100%)',
              border: '1px solid rgba(139,0,0,0.3)',
              borderRadius: 4,
              p: 4,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: '800',
                background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 3,
                textAlign: 'center',
              }}
            >
              Technologies We Love
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'center',
                p: 3,
              }}
            >
              {technologies.map((tech, index) => (
                <motion.div
                  key={tech.name}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  whileHover={{ scale: 1.12, y: -4 }}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip title={tech.desc} arrow placement="top">
                    <Chip
                      label={tech.name}
                      sx={{
                        background: 'linear-gradient(135deg, rgba(139,0,0,0.4), rgba(0,0,0,0.3))',
                        color: 'rgba(255,255,255,0.95)',
                        border: '1px solid rgba(139,0,0,0.5)',
                        fontWeight: 600,
                        fontSize: '1rem',
                        px: 2,
                        py: 2,
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </Tooltip>
                </motion.div>
              ))}
            </Box>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About;